import { message } from 'antd';
import { DEFAULT_MAXIMUM_FILES, FileUploaded, FormWrapper, UploadInput } from 'components/form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
// import { CardTitle } from 'components/claimCard/CardTitle';
import { SelectedClaimTypeObjectType } from '../hooks/useSelectedClaimTypeMapping';
import { breakpoint } from 'styled-components-breakpoint';
import { useFormContext } from 'react-hook-form';
import { ClaimFormState, excludeRemovedDocuments } from '../schema';
import { PreClaimFormState } from '../../preClaim/schema';
import { useCallback, useEffect, useState } from 'react';
import InfoIcon from 'assets/images/icon-info.svg';
import { Typography } from 'antd';
import { MessageType } from '../../../models/MessageType';
const { Text } = Typography;
import type { UploadFile } from 'antd/es/upload/interface';
import Base64Utils from 'utils/Base64';
import { RcFile } from 'antd/es/upload';

interface DocumentsCard {
  formStepIndex?: number;
  selectedClaimTypeObject?: SelectedClaimTypeObjectType;
  allowRemoveDocuments?: boolean;
}

const Wrapper = styled.div`
  display: grid;
  row-gap: 26px;
  margin-top: 4px;
  ${breakpoint('desktop')`
    width: 50%;
  `}
`;

export interface ClaimDocuments {
  receipts?: FileUploaded[];
  // referralLetters?: FileUploaded[];
  otherDocuments?: FileUploaded[];
  prevClaimSettlements?: FileUploaded[];
}

export const getUploadDocumentParams = (values: ClaimDocuments) => {
  return {
    receiptsLength: excludeRemovedDocuments(values?.receipts).length,
    // referralLettersLength: excludeRemovedDocuments(values?.referralLetters).length,
    otherDocumentsLength: excludeRemovedDocuments(values?.otherDocuments).length,
    prevClaimSettlementsLength: excludeRemovedDocuments(values?.prevClaimSettlements).length,
  };
};

export const getNeededRemoveDocuments = (values: ClaimDocuments) => {
  const { receipts, otherDocuments, prevClaimSettlements } = values;
  const isNotUploaded = (uid: string) => {
    const ANT_FILE_UID_PREFIX = 'rc-upload';
    return !uid.startsWith(ANT_FILE_UID_PREFIX);
  };
  const getRemovedDocuments = (documents: FileUploaded[] | undefined) =>
    (documents || [])
      .filter((document: FileUploaded) => document.status === 'removed' && isNotUploaded(document.uid))
      .map((document: FileUploaded) => document.uid);
  return [
    ...getRemovedDocuments(receipts),
    // ...getRemovedDocuments(referralLetters),
    ...getRemovedDocuments(otherDocuments),
    ...getRemovedDocuments(prevClaimSettlements),
  ].join(',');
};

const DocumentsCard = ({
  // formStepIndex = 3,
  selectedClaimTypeObject,
  allowRemoveDocuments = false,
}: DocumentsCard) => {
  const { t } = useTranslation();
  const {
    trigger,
    watch,
    formState,
    // setValue
  } = useFormContext<ClaimFormState | PreClaimFormState>();
  const [disableUpload, setDisableUpload] = useState(false);
  const receipts = watch('receipts');
  // const referralLetters = watch('referralLetters');
  const otherDocuments = watch('otherDocuments');

  const triggerValidationAfterSubmit = useCallback(
    (field: 'receipts') => {
      if (formState.isSubmitted) {
        trigger(field).then();
      }
    },
    [formState.isSubmitted, trigger],
  );

  const handleDisableUploadDocuments = useCallback(() => {
    const receiptsAmount = receipts ? excludeRemovedDocuments(receipts).length : 0;
    // const referralLettersAmount = referralLetters ? excludeRemovedDocuments(referralLetters).length : 0;
    const otherDocumentsAmount = otherDocuments ? excludeRemovedDocuments(otherDocuments).length : 0;
    const totalDocuments = receiptsAmount + otherDocumentsAmount;
    let disabled = false;
    if (totalDocuments >= DEFAULT_MAXIMUM_FILES) {
      disabled = true;
    }
    setDisableUpload(disabled);
    if (receiptsAmount !== DEFAULT_MAXIMUM_FILES && otherDocumentsAmount !== DEFAULT_MAXIMUM_FILES && disabled) {
      message[MessageType.WARNING](t('common.error.noOfFilesExceeds', { amount: DEFAULT_MAXIMUM_FILES }));
    }
  }, [receipts, otherDocuments, t]);

  useEffect(() => {
    handleDisableUploadDocuments();
  }, [handleDisableUploadDocuments]);

  // useEffect(() => {
  //   const hasReferralLetters = (selectedClaimTypeObject?.requiredDocs || []).length > 1;
  //   if (!hasReferralLetters && referralLetters?.length) {
  //     setValue('referralLetters', []);
  //   }
  // }, [referralLetters?.length, selectedClaimTypeObject?.requiredDocs, setValue]);

  // useEffect(() => {
  //   triggerValidationAfterSubmit('referralLetters');
  // }, [triggerValidationAfterSubmit, receipts]);

  useEffect(() => {
    triggerValidationAfterSubmit('receipts');
  }, [triggerValidationAfterSubmit]);

  const handleOnPreview = async (file: UploadFile) => {
    try {
      // 检查文件是否有 originFileObj（实际文件对象）
      const fileToPreview = file.originFileObj || file;

      if (fileToPreview) {
        // 获取文件的 Base64 数据
        const base64Data = await Base64Utils.getBase64FromRcFile(fileToPreview as RcFile);

        // 创建新窗口并显示文件内容
        const win = window.open('', '_blank');
        if (win) {
          // 根据文件类型决定显示方式
          if (file.type?.includes('image/')) {
            // 图片文件：直接显示图片
            win.document.write(`
              <html>
                <head><title>${file.name || 'Preview'}</title></head>
                <body style="margin:0; padding:20px; display:flex; justify-content:center; align-items:center; min-height:100vh; background:#f5f5f5;">
                  <img src="${base64Data}" style="max-width:100%; max-height:90vh; object-fit:contain;" alt="${
              file.name
            }" />
                </body>
              </html>
            `);
          } else if (file.type === 'application/pdf') {
            // PDF 文件：使用 iframe 显示
            win.document.write(`
              <html>
                <head><title>${file.name || 'PDF Preview'}</title></head>
                <body style="margin:0; padding:0;">
                  <iframe src="${base64Data}" style="width:100%; height:100vh; border:none;" />
                </body>
              </html>
            `);
          }
          win.document.close();
        }
      }
    } catch (error) {
      console.error('文件预览失败:', error);
    }
  };

  return (
    // <Card
    //   id="documents"
    //   title={<CardTitle formStepIndex={formStepIndex} title={t('newClaim.supplementaryDocuments.title')} />}
    // >
    <div>
      <Text style={{ fontSize: '16px', display: 'inline-block', marginTop: '20px' }}>
        {t('newClaim.supplementaryDocuments.newReceiptsDesc')}
      </Text>
      <Wrapper>
        {/* <Text>{t('newClaim.supplementaryDocuments.description')}</Text> */}
        <FormWrapper
          name="receipts"
          label={selectedClaimTypeObject?.requiredDocs?.[0] || t('newClaim.supplementaryDocuments.newReceipts')}
          secondaryText={t('newClaim.supplementaryDocuments.uploadingReceiptInfo.description')}
          secondaryTextIcon={<img src={InfoIcon} alt="receiptsIcon" style={{ marginTop: '4px' }} />}
        >
          <UploadInput
            maximumFiles={1}
            accept="image/*,application/pdf"
            name="receipts"
            allowRemoveDocuments={allowRemoveDocuments}
            disabled={disableUpload}
            showButton={receipts?.filter((item) => item.status !== 'removed').length === 1 ? false : true}
            onPreview={handleOnPreview}
          />
        </FormWrapper>

        {/* {(selectedClaimTypeObject?.requiredDocs || []).length > 1 && (
          <FormWrapper label={selectedClaimTypeObject?.requiredDocs[1] || ''} name="referralLetters">
            <UploadInput
              accept="image/*,application/pdf"
              name="referralLetters"
              allowRemoveDocuments={allowRemoveDocuments}
              disabled={disableUpload}
            />
          </FormWrapper>
        )} */}
        {/* <Text>{t('newClaim.supplementaryDocuments.description')}</Text>

        <FormWrapper
          name="otherDocuments"
          optionalText={t('common.optional')}
          label={t('newClaim.supplementaryDocuments.otherDocument')}
          secondaryText={t('newClaim.supplementaryDocuments.uploadingOtherDocumentInfo.description')}
          secondaryTextIcon={<img src={InfoIcon} alt="receiptsIcon" style={{ marginTop: '4px' }} />}
        >
          <UploadInput
            accept="image/*,application/pdf"
            name="otherDocuments"
            allowRemoveDocuments={allowRemoveDocuments}
            disabled={disableUpload}
          />
        </FormWrapper> */}
      </Wrapper>
    </div>
    // </Card>
  );
};

export default DocumentsCard;
